import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map as Mapbox, MapRef, Marker } from 'react-map-gl/mapbox';
import { ModalRef } from 'components/Modal/Modal';
import RectangleSkeleton from 'components/Skeletons/RectangleSkeleton';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import WidgetBox from 'components/widgets/WidgetBox';
import { trackingClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { getMockPropertyViewCameras } from 'mocks/propertyView';
import { ActiveCamera, CameraLatestImage } from 'types/CarTracking';
import { PropertyViewMarker, toFiniteNumber, toPropertyViewMarker } from './propertyViewHelpers';
import ExpandedCameraImageModal from '../../../ExpandedCameraImageModal/ExpandedCameraImageModal';
import styles from './PropertyViewWidget.module.css';
import env from 'env';
import { PROPERTY_VIEW_MAP_CAMERA } from './propertyViewMapCamera';

type PropertyViewWidgetProps = {
    spotUuid: string;
    spotLatitude: string;
    spotLongitude: string;
    isDemoMode?: boolean;
    onVisibilityChange?: (isVisible: boolean) => void;
};

const MAP_CENTER_TOLERANCE = 0.00005;
const DEFAULT_MAP_STYLE = {
    version: 8,
    sources: {
        openstreetmap: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors',
        },
    },
    layers: [
        {
            id: 'openstreetmap',
            type: 'raster',
            source: 'openstreetmap',
        },
    ],
} as const;

function RecenterIcon({ className }: { className?: string }): JSX.Element {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
            <circle cx="12" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M12 2V6M12 18V22M2 12H6M18 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

function CameraMarkerIcon({ className }: { className?: string }): JSX.Element {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
            <path
                d="M17 7h-1.7l-1.3-2h-4l-1.3 2H7c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2Zm-5 9a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z"
                fill="currentColor"
            />
        </svg>
    );
}

function PropertyViewSkeleton(): JSX.Element {
    return (
        <div className={styles.skeletonContainer} data-testid="property-view-skeleton">
            <RectangleSkeleton width="100%" height="460px" />
        </div>
    );
}

function PropertyViewImageLoadingSkeleton(): JSX.Element {
    return (
        <div className={styles.imageModalSkeleton} data-testid="property-view-image-modal-loading">
            <RectangleSkeleton width="100%" height="100%" />
        </div>
    );
}

function PropertyViewWidget({
    spotUuid,
    spotLatitude,
    spotLongitude,
    isDemoMode = false,
    onVisibilityChange,
}: PropertyViewWidgetProps): JSX.Element | null {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [cameras, setCameras] = useState<ActiveCamera[] | null>(null);
    const [showRecenterButton, setShowRecenterButton] = useState(false);
    const [selectedMarkerImage, setSelectedMarkerImage] = useState<CameraLatestImage | null>(null);
    const [isSelectedMarkerImageLoading, setIsSelectedMarkerImageLoading] = useState(false);
    const mapRef = useRef<MapRef>(null);
    const markerImageModalRef = useRef<ModalRef>();

    const fetchCameras = useCallback(async () => {
        if (!spotUuid) {
            return;
        }

        setLoading(true);
        setErrorMessage('');
        try {
            const response = isDemoMode
                ? getMockPropertyViewCameras()
                : await trackingClient.getActiveCameras(spotUuid);
            setCameras(response);
        } catch (error) {
            if (HTTPError.isHTTPError(error)) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage(error?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [isDemoMode, spotUuid]);

    useEffect(() => {
        fetchCameras();
    }, [fetchCameras]);

    const markers = useMemo(
        () => (cameras ?? []).map(toPropertyViewMarker).filter((marker): marker is PropertyViewMarker => !!marker),
        [cameras]
    );

    const spotCenter = useMemo(() => {
        const latitude = toFiniteNumber(spotLatitude);
        const longitude = toFiniteNumber(spotLongitude);
        if (latitude === null || longitude === null) {
            return null;
        }

        return { latitude, longitude };
    }, [spotLatitude, spotLongitude]);

    const mapCenter = useMemo(() => {
        if (markers.length > 0) {
            const latitude = markers.reduce((sum, marker) => sum + marker.latitude, 0) / markers.length;
            const longitude = markers.reduce((sum, marker) => sum + marker.longitude, 0) / markers.length;
            return { latitude, longitude };
        }

        return spotCenter;
    }, [markers, spotCenter]);

    const mapCamera = PROPERTY_VIEW_MAP_CAMERA;

    const isLoading = loading || !cameras;
    const shouldHideWidget = !isLoading && !errorMessage && markers.length === 0;

    useEffect(() => {
        onVisibilityChange?.(!shouldHideWidget);
    }, [onVisibilityChange, shouldHideWidget]);

    const isNearMapCenter = useCallback(
        (latitude: number, longitude: number) => {
            if (!mapCenter) {
                return true;
            }

            return (
                Math.abs(latitude - mapCenter.latitude) <= MAP_CENTER_TOLERANCE &&
                Math.abs(longitude - mapCenter.longitude) <= MAP_CENTER_TOLERANCE
            );
        },
        [mapCenter]
    );

    const handleMapMoveEnd = useCallback(
        (event: { viewState: { latitude: number; longitude: number } }) => {
            setShowRecenterButton(!isNearMapCenter(event.viewState.latitude, event.viewState.longitude));
        },
        [isNearMapCenter]
    );

    const handleRecenterMap = useCallback(() => {
        if (!mapCenter) {
            return;
        }

        mapRef.current?.flyTo({
            center: [mapCenter.longitude, mapCenter.latitude],
            zoom: mapCamera.zoom,
            pitch: mapCamera.pitch,
            bearing: mapCamera.bearing,
            duration: 600,
            essential: true,
        });
        setShowRecenterButton(false);
    }, [mapCenter, mapCamera.bearing, mapCamera.pitch, mapCamera.zoom]);

    useEffect(() => {
        setShowRecenterButton(false);
    }, [mapCenter?.latitude, mapCenter?.longitude]);

    const handleMarkerClick = useCallback(
        async (marker: PropertyViewMarker) => {
            const markerImage = marker.latestImage;
            setSelectedMarkerImage(markerImage);
            setIsSelectedMarkerImageLoading(!markerImage);
            markerImageModalRef.current?.openModal();

            if (markerImage) {
                return;
            }

            try {
                const response = await trackingClient.getCameraImageAt(marker.id, spotUuid);
                if (!response) {
                    setSelectedMarkerImage(null);
                    return;
                }
                const imageSrc = response.thumbnail_url || response.image_url;
                const fallbackImageSrc = response.image_url;
                const capturedAtEpoch = Math.floor(new Date(response.captured_at).getTime() / 1000);
                if (Number.isNaN(capturedAtEpoch)) {
                    setSelectedMarkerImage(null);
                    return;
                }

                const loadedImage: CameraLatestImage = {
                    camera_id: marker.id,
                    name: marker.label,
                    image_url: fallbackImageSrc,
                    thumbnail_url: imageSrc,
                    event_datetime: capturedAtEpoch,
                };
                setSelectedMarkerImage(loadedImage);
                setCameras(
                    (prevCameras) =>
                        prevCameras?.map((camera) =>
                            camera.id === marker.id ? { ...camera, latest_image: loadedImage } : camera
                        ) || prevCameras
                );
            } catch (_error) {
                setSelectedMarkerImage(null);
            } finally {
                setIsSelectedMarkerImageLoading(false);
            }
        },
        [spotUuid]
    );

    const closeMarkerImageModal = useCallback(() => {
        setSelectedMarkerImage(null);
        setIsSelectedMarkerImageLoading(false);
    }, []);

    const selectedMarkerImageSrc = selectedMarkerImage?.thumbnail_url || selectedMarkerImage?.image_url || null;
    const selectedMarkerImageFallbackSrc = selectedMarkerImage?.image_url || selectedMarkerImageSrc;

    const content = useMemo(() => {
        if (errorMessage) {
            return <WidgetErrorMessage message={errorMessage} onRetry={fetchCameras} />;
        }

        if (isLoading) {
            return <PropertyViewSkeleton />;
        }

        if (!mapCenter) {
            return <WidgetErrorMessage message="Spot coordinates are unavailable." onRetry={fetchCameras} />;
        }

        return (
            <div className={styles.content}>
                <div className={styles.mapContainer}>
                    <Mapbox
                        ref={mapRef}
                        key={`${mapCenter.latitude}-${mapCenter.longitude}`}
                        mapStyle={env.MAPBOX_STYLE || DEFAULT_MAP_STYLE}
                        initialViewState={{
                            latitude: mapCenter.latitude,
                            longitude: mapCenter.longitude,
                            zoom: mapCamera.zoom,
                            pitch: mapCamera.pitch,
                            bearing: mapCamera.bearing,
                        }}
                        minZoom={mapCamera.minZoom}
                        maxZoom={mapCamera.maxZoom}
                        maxPitch={mapCamera.maxPitch}
                        minPitch={mapCamera.minPitch}
                        style={{ width: '100%', height: '100%' }}
                        data-testid="property-view-map"
                        mapboxAccessToken={env.MAPBOX_TOKEN}
                        onMoveEnd={handleMapMoveEnd}
                    >
                        {markers.map((marker) => {
                            return (
                                <Marker
                                    key={marker.id}
                                    latitude={marker.latitude}
                                    longitude={marker.longitude}
                                    anchor="bottom"
                                    data-testid="property-view-marker"
                                >
                                    <button
                                        type="button"
                                        className={styles.cameraMarker}
                                        title={marker.label}
                                        aria-label={marker.label}
                                        data-testid={`property-view-marker-button-${marker.id}`}
                                        onClick={() => void handleMarkerClick(marker)}
                                    >
                                        <CameraMarkerIcon className={styles.cameraIcon} />
                                    </button>
                                </Marker>
                            );
                        })}
                    </Mapbox>
                    {showRecenterButton ? (
                        <button
                            type="button"
                            className={styles.recenterButton}
                            onClick={handleRecenterMap}
                            data-testid="property-view-recenter-button"
                            aria-label="Recenter map"
                            title="Recenter map"
                        >
                            <RecenterIcon className={styles.recenterIcon} />
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }, [
        errorMessage,
        fetchCameras,
        handleMarkerClick,
        handleMapMoveEnd,
        handleRecenterMap,
        isLoading,
        mapCamera,
        mapCenter?.latitude,
        mapCenter?.longitude,
        markers,
        showRecenterButton,
    ]);

    if (shouldHideWidget) {
        return null;
    }

    return (
        <WidgetBox title="Property View">
            <div className={styles.widgetBody} data-testid="property-view-widget">
                {content}
                <ExpandedCameraImageModal
                    modalRef={markerImageModalRef}
                    imageSrc={isSelectedMarkerImageLoading ? null : selectedMarkerImageSrc}
                    fallbackSrc={isSelectedMarkerImageLoading ? null : selectedMarkerImageFallbackSrc}
                    onClose={closeMarkerImageModal}
                    fallback={
                        isSelectedMarkerImageLoading ? (
                            <PropertyViewImageLoadingSkeleton />
                        ) : (
                            <p className={styles.imageModalMessage} data-testid="property-view-image-modal-empty">
                                No recent image available for this camera yet.
                            </p>
                        )
                    }
                />
            </div>
        </WidgetBox>
    );
}

export default PropertyViewWidget;
